<template>
    <div class="wellness-section">
        <div class="well-toolbar">
            <select-option-students :custom-options="viewOptions" :value="view" @change="view = $event" />
            <div v-if="view === 'table'" class="well-search">
                <v-icon size="16">mdi-magnify</v-icon>
                <input type="text" :placeholder="$t('common.search')" :value="search"
                    @input="search = $event.target.value">
            </div>
            <button-tooltip bottom :tooltip="$t('tooltips.filter')" class="ml-auto">
                <template #activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" icon class="well-icon-btn" :class="{ active: !!filter }"
                        @click="showFilter">
                        <v-icon size="18">mdi-filter-outline</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>
        </div>

        <div v-show="view === 'table'" class="well-table-section">
            <div class="well-table-wrap">
                <table class="well-table">
                    <thead>
                        <tr>
                            <th>{{ $t('common.name') }}</th>
                            <th>{{ $t('common.lastWellness') }}</th>
                            <th>{{ $t('statistics.last_value') }}</th>
                            <th>{{ $t('common.alert') }}</th>
                            <th>{{ $t('questions.test') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in pagedItems" :key="item.id" class="well-row" @click="onRowClick(item)">
                            <td class="td-name">{{ item.name }}</td>
                            <td>
                                <wellness-dots :wellness="item.lastWellness" :highlight-date="hoveredWellnessDate"
                                    @hover="hoveredWellnessDate = $event" />
                            </td>
                            <td>
                                <div v-if="item.preInfo" class="ws-chips">
                                    <v-tooltip v-for="v in dimensions" :key="v.key" top>
                                        <template #activator="{ on }">
                                            <span v-on="on" class="ws-chip">{{ item.preInfo[v.key] || 0 }}</span>
                                        </template>
                                        <span>{{ v.label }}</span>
                                    </v-tooltip>
                                    <span class="ws-total" :class="totalClass(item)">{{ sumOf(item.preInfo) }}</span>
                                </div>
                                <span v-else class="well-muted">—</span>
                            </td>
                            <td>
                                <span v-if="item.bodyItems.length > 0">
                                    {{ item.bodyItems.firstValue }}
                                    <span v-if="item.bodyItems.length > 1" class="well-muted">
                                        (+{{ item.bodyItems.length - 1 }})
                                    </span>
                                </span>
                                <span v-else class="well-muted">—</span>
                            </td>
                            <td>
                                <div v-if="item.test" class="well-test">
                                    <truncated-text :text="item.test" />
                                </div>
                                <span v-else class="well-muted">—</span>
                            </td>
                        </tr>
                        <tr v-if="pagedItems.length === 0">
                            <td colspan="5" class="well-empty">{{ $t('common.no_data_yet') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <kimsa-pagination v-if="pageCount > 1" @page="page = $event" :pageCount="pageCount" :value="page" />
        </div>

        <div v-if="view === 'chart'" class="well-graphic" :class="graphicClass">
            <stat-card class="well-main-card" expandable :expanded="expandedCard === 0"
                @toggle:expand="toggleExpand(0)">
                <template #header>
                    <kimsa-combobox v-if="chartType === 'bars'" class="well-metric-select" dense solo no-object
                        :items="metricOptions" :value="metric" @change="metric = $event" height="30" />
                    <span v-else class="well-card-title">{{ $t('statistics.radar_wellness') }}</span>
                    <div class="well-hdr-actions">
                        <group-by-selector v-if="chartType === 'bars'" no-monthly :value="groupBy"
                            @change="groupBy = $event" />
                        <segmented-tabs class="well-views" :options="chartTypeOptions" :value="chartType"
                            @change="chartType = $event" />
                    </div>
                </template>
                <div v-if="!hasWellnessData" class="well-chart-empty">{{ $t('common.without_data') }}</div>
                <template v-else-if="chartType === 'bars'">
                    <div class="well-pills">
                        <span v-for="d in activeDimensions" :key="d.key" class="well-pill">
                            <i :style="{ background: d.color }" />{{ d.label }}
                        </span>
                    </div>
                    <div class="well-bar-wrap">
                        <wellness-stacked-chart :labels="barLabels" :datasets="barDatasets" :max="barMax"
                            :styles="chartStyles" />
                    </div>
                </template>
                <template v-else>
                    <div class="well-radar-wrap">
                        <radar-chart :labels="radarLabels" :datasets="radarDatasets" :max="5" :styles="chartStyles" />
                    </div>
                    <div class="well-radar-legend">
                        <span class="wr-leg wr-leg--last">{{ $t('statistics.last') }}</span>
                        <span class="wr-leg wr-leg--avg">{{ $t('statistics.period_average') }}</span>
                    </div>
                </template>
            </stat-card>

            <stat-card class="well-dist-card" expandable :expanded="expandedCard === 1"
                @toggle:expand="toggleExpand(1)">
                <template #header>
                    <span class="well-card-title">
                        {{ isPainView ? $t('statistics.pain_map') : $t('statistics.wellness_distribution') }}
                    </span>
                    <div class="well-hdr-actions">
                        <span v-if="distributionNote" class="well-hdr-note">{{ distributionNote }}</span>
                        <segmented-tabs class="well-views" :options="distributionOptions" :value="distributionView"
                            @change="distributionView = $event" />
                    </div>
                </template>
                <template v-if="isPainView">
                    <div v-if="painStudents" class="well-pain-wrap">
                        <body-map readonly :levels="painLevels" :values="painCounts" />
                    </div>
                    <div v-else class="well-chart-empty">{{ $t('statistics.no_pain_today') }}</div>
                </template>
                <skeleton-loader v-else-if="loadingBaseline" type="team-bars" />
                <wellness-distribution-chart v-else-if="hasDistributionData" :rows="distributionRows"
                    :max="distributionMax" />
                <div v-else class="well-chart-empty">{{ $t('statistics.no_wellness_distribution') }}</div>
            </stat-card>
        </div>

        <filter-wellness-dialog ref="filter" @filter="onFilter" />
        <questionnaire-summary ref="summary" />
    </div>
</template>

<script>
import SelectOptionStudents from '@/components/coach/SelectOptionStudents'
import WellnessDots from '@/components/student/WellnessDots'
import QuestionnaireSummary from '@/components/student/QuestionnaireSummary'
import KimsaPagination from '@/components/common/KimsaPagination'
import KimsaCombobox from '@/components/common/inputs/KimsaCombobox'
import TruncatedText from '@/components/common/TruncatedText'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import RadarChart from '@/components/common/RadarChart'
import WellnessStackedChart from '@/components/common/WellnessStackedChart'
import GroupBySelector from '@/components/statistics/GroupBySelector'
import FilterWellnessDialog from '@/components/common/FilterWellnessDialog'
import StatCard from '@/components/statistics/StatCard'
import SegmentedTabs from '@/components/common/SegmentedTabs'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import WellnessDistributionChart from '@/components/statistics/WellnessDistributionChart'
import BodyMap from '@/components/common/BodyMap'
import { mapState } from 'vuex'
import { getWellnessScore, getWellnessStatusKey, filterValidWellness, wellnessHexColors, wellnessColors } from '@/utils/Routine'
import { fetchStudentsAdvances } from '@/utils/Statistics'
import { parseAdvanceBodyZones } from '@/utils/BodyMap'

const DISTRIBUTION_KEYS = ['veryGood', 'good', 'somewhatGood', 'normal', 'somewhatBad', 'bad', 'veryBad']
const MIN_SCORE_RECORDS = 6
const BASELINE_WEEKS = 2
const DISTRIBUTION_MIN_AXIS = 30

export default {
    name: 'StatisticsWellness',
    components: {
        SelectOptionStudents, WellnessDots, QuestionnaireSummary, KimsaPagination, KimsaCombobox,
        TruncatedText, ButtonTooltip, RadarChart, WellnessStackedChart, GroupBySelector, FilterWellnessDialog,
        StatCard, SegmentedTabs, SkeletonLoader, WellnessDistributionChart, BodyMap,
    },
    props: {
        docs: {
            type: Array,
            default: () => []
        },
        teamStudents: {
            type: Array,
            default: () => []
        },
        teamId: {
            type: String,
            default: null,
        },
        startDate: {
            type: String,
            default: null,
        },
        endDate: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data(vm) {
        return {
            view: 'table',
            search: '',
            page: 1,
            perPage: 10,
            metric: 'general',
            groupBy: 'daily',
            chartType: 'bars',
            distributionView: 'wellness',
            filter: null,
            hoveredWellnessDate: null,
            expandedCard: null,
            baselineDocs: [],
            loadingBaseline: false,
            baselineCache: { key: null, data: null },
            viewOptions: [
                { name: vm.$t('common.table'), value: 'table', icon: 'mdi-table' },
                { name: vm.$t('common.chart'), value: 'chart', icon: 'mdi-chart-box-outline' },
            ],
        }
    },
    computed: {
        ...mapState(['teams']),
        graphicClass(vm = this) {
            if (vm.expandedCard === 0) return 'well-graphic--main'
            if (vm.expandedCard === 1) return 'well-graphic--dist'
            return null
        },
        chartTypeOptions(vm = this) {
            return [
                { name: vm.$t('statistics.bar_chart'), value: 'bars' },
                { name: vm.$t('statistics.radar_chart'), value: 'radar' },
            ]
        },
        currentTeam(vm = this) {
            return vm.teamId ? vm.teams.find(t => t.id === vm.teamId) : null
        },
        subgroups(vm = this) {
            return (vm.currentTeam?.groups || []).filter(g => g && g.name)
        },
        allowedStudentIds(vm = this) {
            if (!vm.filter) return null
            const { subgroups, studentIds } = vm.filter
            let pool = null
            if (subgroups.length) {
                pool = new Set()
                subgroups.forEach(sgId => {
                    const sg = vm.subgroups.find(s => s.id === sgId)
                    if (sg) (sg.students || []).forEach(id => pool.add(id))
                })
            }
            if (studentIds.length) {
                return pool ? new Set(studentIds.filter(id => pool.has(id))) : new Set(studentIds)
            }
            return pool
        },
        dimensions(vm = this) {
            return [
                { key: 'sleep_night', label: vm.$t('statistics.sleep_night'), color: wellnessColors.sleep_night },
                { key: 'muscles_hurt', label: vm.$t('statistics.muscles_hurt'), color: wellnessColors.muscles_hurt },
                { key: 'tired', label: vm.$t('statistics.tired'), color: wellnessColors.tired },
                { key: 'stressed', label: vm.$t('statistics.stressed'), color: wellnessColors.stressed },
                { key: 'energy', label: vm.$t('statistics.energy'), color: wellnessColors.energy },
            ]
        },
        chartStyles() {
            return { position: 'relative', height: '100%', width: '100%' }
        },
        selectedDate(vm = this) {
            return vm.endDate
        },
        dayDocs(vm = this) {
            if (!vm.selectedDate) return []
            return vm.docs.filter(doc => doc?.rawDate?.full === vm.selectedDate)
        },
        docsByStudent(vm = this) {
            const byStudent = {}
            vm.dayDocs.forEach(doc => {
                const sid = doc.studentId
                if (!sid) return
                if (!byStudent[sid]) byStudent[sid] = []
                byStudent[sid].push(doc)
            })
            return byStudent
        },
        items(vm = this) {
            const rows = []
            for (const user of vm.teamStudents) {
                const sid = user?.objectID
                if (!sid) continue
                const studentDocs = vm.docsByStudent[sid] || []
                const lastWithPre = studentDocs.find(d => d?.advance?.pre && d?.advance?.preInfo)
                const lastDoc = lastWithPre || studentDocs[0] || null

                const preSummary = lastDoc?.advance?.preSummary || []
                const bodyItems = preSummary.filter(p => p.type === 'body')
                const firstBodyItem = bodyItems?.[0]?.value?.[0]?.values?.[0] || bodyItems?.[0]?.value || null
                const totalBodyItemValues = (Array.isArray(bodyItems?.[0]?.value) ? bodyItems[0].value : []).reduce((acc, item) => {
                    return acc + (item?.values?.length || 0);
                }, 0) || 0;
                const testItem = preSummary.find(p => p.type === 'statistic' || p.isStatistic)

                const history = filterValidWellness(user.lastWellness)
                const scores = history.length >= 6
                    ? getWellnessScore(history, history.length - 1)
                    : null

                rows.push({
                    id: sid,
                    name: user.name || user.email || '?',
                    lastWellness: user.lastWellness || [],
                    preInfo: lastDoc?.advance?.preInfo || null,
                    bodyItems: { firstValue: firstBodyItem, length: totalBodyItemValues },
                    test: testItem?.value || '',
                    preSummary,
                    scores,
                })
            }
            return rows.sort((a, b) => String(a.name).localeCompare(String(b.name)))
        },
        filteredItems(vm = this) {
            let list = vm.items
            if (vm.allowedStudentIds) list = list.filter(i => vm.allowedStudentIds.has(i.id))
            if (vm.search) {
                const q = vm.search.toLowerCase()
                list = list.filter(i => String(i.name || '').toLowerCase().includes(q))
            }
            return list
        },
        pageCount(vm = this) {
            return vm.filteredItems.length > 0 ? Math.ceil(vm.filteredItems.length / vm.perPage) : 1
        },
        pagedItems(vm = this) {
            const start = (vm.page - 1) * vm.perPage
            return vm.filteredItems.slice(start, start + vm.perPage)
        },
        docsWithPre(vm = this) {
            return vm.docs.filter(d => {
                if (!d?.advance?.preInfo) return false
                if (vm.allowedStudentIds && !vm.allowedStudentIds.has(d.studentId)) return false
                return true
            })
        },
        hasWellnessData(vm = this) {
            return vm.docsWithPre.length > 0
        },
        radarData(vm = this) {
            const dims = vm.dimensions
            const avg = dims.map(d => vm.mean(vm.docsWithPre.map(doc => Number(doc.advance.preInfo[d.key] || 0))))

            const byStudent = {}
            vm.docsWithPre.forEach(doc => {
                const cur = byStudent[doc.studentId]
                if (!cur || (doc.rawDate?.full || '') > (cur.rawDate?.full || '')) byStudent[doc.studentId] = doc
            })
            const lastDocs = Object.values(byStudent)
            const last = dims.map(d => vm.mean(lastDocs.map(doc => Number(doc.advance.preInfo[d.key] || 0))))

            return { last, avg }
        },
        radarLabels(vm = this) {
            return vm.dimensions.map(d => d.label)
        },
        radarDatasets(vm = this) {
            const r = vm.radarData
            const last = r.last.map(vm.round1)
            const avg = r.avg.map(vm.round1)
            return [
                {
                    label: vm.$t('statistics.last'), data: last, dataReal: last,
                    backgroundColor: 'rgba(204,255,51,0.12)', borderColor: '#CCFF33', borderWidth: 2,
                    pointBackgroundColor: '#CCFF33', pointBorderColor: '#0C0E12', pointRadius: 3, pointHoverRadius: 5,
                },
                {
                    label: vm.$t('statistics.period_average'), data: avg, dataReal: avg,
                    backgroundColor: 'rgba(51,153,153,0.08)', borderColor: '#339999', borderWidth: 1.5, borderDash: [5, 3],
                    pointBackgroundColor: '#339999', pointBorderColor: '#0C0E12', pointRadius: 2, pointHoverRadius: 4,
                },
            ]
        },
        metricOptions(vm = this) {
            return [
                { name: vm.$t('statistics.wellness_general'), value: 'general' },
                ...vm.dimensions.map(d => ({ name: d.label, value: d.key })),
            ]
        },
        activeDimensions(vm = this) {
            return vm.metric === 'general' ? vm.dimensions : vm.dimensions.filter(d => d.key === vm.metric)
        },
        barBuckets(vm = this) {
            const map = {}
            vm.docsWithPre.forEach(doc => {
                if (!doc?.rawDate?.full) return
                let m = vm.$moment(doc.rawDate.full)
                if (!m.isValid()) return
                if (vm.groupBy === 'weekly') m = m.startOf('week')
                const key = m.format('YYYY-MM-DD')
                if (!map[key]) map[key] = { key, label: m.format('DD MMM').replace('.', ''), docs: [] }
                map[key].docs.push(doc)
            })
            return Object.values(map).sort((a, b) => a.key < b.key ? -1 : 1)
        },
        barLabels(vm = this) {
            return vm.barBuckets.map(b => b.label)
        },
        barDatasets(vm = this) {
            return vm.activeDimensions.map(d => ({
                label: d.label,
                backgroundColor: d.color,
                data: vm.barBuckets.map(b => vm.round1(vm.mean(b.docs.map(doc => Number(doc.advance.preInfo[d.key] || 0))))),
                stack: 'wellness',
            }))
        },
        barMax(vm = this) {
            return vm.metric === 'general' ? undefined : 5
        },
        scopeStudentIds(vm = this) {
            return vm.teamStudents.map(s => s?.objectID).filter(Boolean)
        },
        baselineKey(vm = this) {
            if (!vm.startDate || !vm.scopeStudentIds.length) return null
            return `${vm.coachId}|${vm.startDate}|${vm.scopeStudentIds.join(',')}`
        },
        wellnessSeries(vm = this) {
            const byStudent = {}
            const seen = new Set()

            const add = doc => {
                const preInfo = doc?.advance?.preInfo
                const date = doc?.rawDate?.full
                const sid = doc?.studentId
                if (!doc?.advance?.pre || !preInfo || !date || !sid) return
                if (doc.id && seen.has(doc.id)) return
                if (doc.id) seen.add(doc.id)
                if (!byStudent[sid]) byStudent[sid] = []
                byStudent[sid].push({ date, order: Number(doc.routineNumber || 1), ...preInfo })
            }

            vm.baselineDocs.forEach(add)
            vm.docs.forEach(add)

            Object.values(byStudent).forEach(list => {
                list.sort((a, b) => a.date === b.date ? a.order - b.order : a.date.localeCompare(b.date))
            })
            return byStudent
        },
        classifiedSeries(vm = this) {
            const result = {}
            Object.entries(vm.wellnessSeries).forEach(([studentId, list]) => {
                if (list.length < MIN_SCORE_RECORDS) return
                if (vm.allowedStudentIds && !vm.allowedStudentIds.has(studentId)) return

                const entries = []
                for (let i = MIN_SCORE_RECORDS - 1; i < list.length; i++) {
                    const date = list[i].date
                    if (vm.startDate && date < vm.startDate) continue
                    if (vm.endDate && date > vm.endDate) continue
                    entries.push({ date, key: getWellnessStatusKey(getWellnessScore(list, i)) })
                }
                if (entries.length) result[studentId] = entries
            })
            return result
        },
        todayDistribution(vm = this) {
            const counts = {}
            let total = 0
            Object.values(vm.classifiedSeries).forEach(entries => {
                const onDate = entries.filter(e => e.date === vm.selectedDate)
                if (!onDate.length) return
                const key = onDate[onDate.length - 1].key
                counts[key] = (counts[key] || 0) + 1
                total++
            })
            return { counts, total }
        },
        periodDistribution(vm = this) {
            const counts = {}
            let total = 0
            Object.values(vm.classifiedSeries).forEach(entries => entries.forEach(e => {
                counts[e.key] = (counts[e.key] || 0) + 1
                total++
            }))
            return { counts, total }
        },
        distributionRows(vm = this) {
            const today = vm.todayDistribution
            const period = vm.periodDistribution
            return DISTRIBUTION_KEYS.map(key => {
                const count = today.counts[key] || 0
                return {
                    key,
                    label: vm.$t(`common.wellnessStatus.${key}`),
                    color: wellnessHexColors[key],
                    count,
                    pct: today.total ? count / today.total * 100 : 0,
                    avgPct: period.total ? (period.counts[key] || 0) / period.total * 100 : 0,
                }
            })
        },
        distributionMax(vm = this) {
            const peak = Math.max(0, ...vm.distributionRows.map(r => Math.max(r.pct, r.avgPct)))
            return Math.max(DISTRIBUTION_MIN_AXIS, Math.ceil(peak / 10) * 10)
        },
        distributionStudents(vm = this) {
            return vm.todayDistribution.total
        },
        hasDistributionData(vm = this) {
            return vm.periodDistribution.total > 0
        },
        distributionOptions(vm = this) {
            return [
                { name: vm.$t('common.wellness'), value: 'wellness' },
                { name: vm.$t('statistics.pain'), value: 'pain' },
            ]
        },
        isPainView(vm = this) {
            return vm.distributionView === 'pain'
        },
        respondedToday(vm = this) {
            return Object.entries(vm.docsByStudent).filter(([studentId, docs]) => {
                if (vm.allowedStudentIds && !vm.allowedStudentIds.has(studentId)) return false
                return docs.some(doc => doc?.advance?.pre)
            }).length
        },
        painToday(vm = this) {
            const counts = {}
            let students = 0
            Object.entries(vm.docsByStudent).forEach(([studentId, docs]) => {
                if (vm.allowedStudentIds && !vm.allowedStudentIds.has(studentId)) return

                const zones = {}
                docs.forEach(doc => Object.assign(zones, parseAdvanceBodyZones(doc?.advance)))
                const ids = Object.keys(zones)
                if (!ids.length) return

                students++
                ids.forEach(id => { counts[id] = (counts[id] || 0) + 1 })
            })
            return { counts, students }
        },
        painCounts(vm = this) {
            return vm.painToday.counts
        },
        painLevels(vm = this) {
            const levels = {}
            Object.entries(vm.painCounts).forEach(([id, count]) => { levels[id] = Math.min(5, count) })
            return levels
        },
        painStudents(vm = this) {
            return vm.painToday.students
        },
        distributionNote(vm = this) {
            if (!vm.isPainView) {
                return vm.distributionStudents
                    ? vm.$t('statistics.students_answered', { count: vm.distributionStudents })
                    : null
            }
            if (!vm.painStudents) return null
            const pct = vm.respondedToday ? Math.round(vm.painStudents / vm.respondedToday * 100) : 0
            return vm.$t('statistics.students_in_pain', { count: vm.painStudents, pct })
        },
    },
    methods: {
        async loadBaseline() {
            const vm = this
            const key = vm.baselineKey
            if (!key) {
                vm.baselineDocs = []
                return
            }
            if (vm.baselineCache.key === key) {
                vm.baselineDocs = vm.baselineCache.data
                return
            }

            vm.loadingBaseline = true
            try {
                const end = vm.$moment(vm.startDate).subtract(1, 'day').format('YYYY-MM-DD')
                const start = vm.$moment(vm.startDate).subtract(BASELINE_WEEKS, 'weeks').format('YYYY-MM-DD')
                const advances = await fetchStudentsAdvances({
                    coachId: vm.coachId,
                    studentIds: vm.scopeStudentIds,
                    start,
                    end,
                })
                const allowed = new Set(vm.scopeStudentIds)
                const data = advances.filter(a => allowed.has(a.studentId))
                vm.baselineDocs = data
                vm.baselineCache = { key, data }
            } catch (err) {
                console.log('loadBaseline error', err)
                vm.baselineDocs = []
            } finally {
                vm.loadingBaseline = false
            }
        },
        toggleExpand(index) {
            const vm = this
            vm.expandedCard = vm.expandedCard === index ? null : index
        },
        mean(arr) {
            return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0
        },
        round1(value) {
            return Math.round(value * 10) / 10
        },
        sumOf(preInfo) {
            if (!preInfo) return 0
            return this.dimensions.reduce((s, d) => s + Number(preInfo[d.key] || 0), 0)
        },
        totalClass(item) {
            if (!item.scores) return 'ws-total--md'
            const key = getWellnessStatusKey(item.scores)
            if (['veryGood', 'good', 'somewhatGood'].includes(key)) return 'ws-total--hi'
            if (['veryBad', 'bad', 'somewhatBad'].includes(key)) return 'ws-total--lo'
            return 'ws-total--md'
        },
        onRowClick(item) {
            const vm = this
            if (!item?.preSummary || item.preSummary.length === 0) return
            vm.$refs.summary.show(item.preSummary)
        },
        showFilter() {
            const vm = this
            vm.$refs.filter.show({
                subgroups: vm.subgroups,
                students: vm.teamStudents,
                filter: vm.filter,
            })
        },
        onFilter(filter = null) {
            this.filter = filter
        },
    },
    watch: {
        search() {
            this.page = 1
        },
        filter() {
            this.page = 1
        },
        selectedDate() {
            this.page = 1
        },
        baselineKey() {
            if (this.view === 'chart') this.loadBaseline()
        },
        view(val) {
            if (val === 'chart') this.loadBaseline()
        },
    },
}
</script>

<style scoped>
.wellness-section {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.well-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 0 14px;
}

.well-search {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 12px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    flex: 1 1 180px;
    max-width: 260px;
    color: var(--v-txtMuted-base);
}

.well-search input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    font-size: 13px;
    font-family: inherit;
    color: var(--v-txtPrimary-base);
}

.well-search input::placeholder {
    color: var(--v-txtMuted-base);
}

.well-icon-btn.v-btn {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base) !important;
    color: var(--v-txtMuted-base) !important;
}

.well-icon-btn.v-btn.active {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base) !important;
    background: rgba(204, 255, 51, 0.08) !important;
}

.well-table-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.well-table-wrap {
    overflow-x: auto;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
}

.well-table {
    width: 100%;
    border-collapse: collapse;
}

.well-table thead th {
    background: var(--v-surface2-base);
    padding: 10px 14px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--v-txtMuted-base);
    border-bottom: 1px solid var(--v-outline-base);
    text-align: left;
    white-space: nowrap;
}

.well-table tbody tr {
    border-bottom: 1px solid var(--v-outline-base);
    transition: background 0.12s;
}

.well-table tbody tr:last-child {
    border-bottom: none;
}

.well-table tbody tr:hover {
    background: var(--v-surface2-base);
}

.well-row {
    cursor: pointer;
}

.well-table td {
    height: 52px;
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    vertical-align: middle;
}

.well-table td.td-name {
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
}

.well-row:hover td.td-name {
    color: var(--v-primary-base);
    text-decoration: underline;
    text-underline-offset: 3px;
}

.theme--light .well-row:hover td.td-name {
    color: var(--v-accent-base);
}

.well-muted {
    color: var(--v-txtMuted-base);
}

.well-test {
    max-width: 220px;
}

.ws-chips {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: nowrap;
}

.ws-chip {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.ws-total {
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    margin-left: 4px;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.ws-total--hi {
    background: var(--v-accent-base);
    color: #FFFFFF;
}

.ws-total--md {
    background: #F5C842;
    color: #0D1117;
}

.ws-total--lo {
    background: var(--v-error-base);
    color: #FFFFFF;
}

.well-empty {
    text-align: center;
    padding: 32px;
    color: var(--v-txtMuted-base);
    font-size: 13px;
}

.well-graphic {
    display: flex;
    gap: 14px;
    height: 380px;
}

.well-main-card,
.well-dist-card {
    flex: 1;
    min-width: 0;
    transition: flex 0.3s;
}

.well-graphic--main .well-main-card,
.well-graphic--dist .well-dist-card {
    flex: 1.8;
}

.well-card-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.well-hdr-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.well-hdr-note {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: normal;
    text-transform: none;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.well-views {
    width: 116px;
}

.well-radar-wrap {
    flex: 1;
    min-height: 0;
}

.well-pain-wrap {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    justify-content: center;
}

.well-pain-wrap ::v-deep .body-map-canvas {
    max-width: 300px;
}

.well-radar-legend {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 6px;
}

.wr-leg {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 600;
}

.wr-leg::before {
    content: '';
    width: 16px;
    height: 0;
    border-top-width: 2px;
    border-top-style: solid;
}

.wr-leg--last {
    color: #CCFF33;
}

.wr-leg--last::before {
    border-top-color: #CCFF33;
}

.wr-leg--avg {
    color: var(--v-accent-base);
}

.wr-leg--avg::before {
    border-top-style: dashed;
    border-top-color: var(--v-accent-base);
}

.well-metric-select {
    flex: 1;
    min-width: 0;
}

.well-metric-select ::v-deep .v-input,
.well-metric-select ::v-deep .v-text-field {
    margin: 0;
    padding: 0;
}

.well-metric-select ::v-deep .v-input__control,
.well-metric-select ::v-deep .v-input__slot {
    min-height: 30px !important;
}

.well-metric-select ::v-deep .v-input__slot {
    padding: 0 10px !important;
}

.well-metric-select ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.well-metric-select ::v-deep .v-input__append-inner {
    margin-top: 0;
    align-self: center;
}

.well-pills {
    display: flex;
    gap: 4px 10px;
    flex-wrap: wrap;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.well-pill {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.well-pill i {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
}

.well-bar-wrap {
    flex: 1;
    min-height: 0;
}

.well-chart-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

@media (max-width: 860px) {
    .well-graphic {
        flex-direction: column;
        height: auto;
    }

    .well-main-card,
    .well-dist-card,
    .well-graphic--main .well-main-card,
    .well-graphic--dist .well-dist-card {
        flex: none;
        height: 340px;
    }
}
</style>
